import React, { useState, useMemo, useCallback } from 'react';

interface ActivitySlot {
  id: number;
  date: string;
  startTime: string;
  endTime: string;
  instructorId: number;
  instructorName: string;
  topicMasterId: number;
  topicName: string;
  agenda: string;
  batchIds: number[];
  zoomMeetingId: string | null;
  roomId?: number;
  roomName?: string;
  centreId?: number;
  centreName?: string;
}

interface SlotFilters {
  startDate: string;
  endDate: string;
  instructorId: string;
  topicId: string;
  roomId: string;
  centreId: string;
  searchText: string;
}

export type GroupByMode = 'all' | 'by-instructor' | 'by-room' | 'by-centre' | 'by-date';

export interface SlotGroup {
  key: string;
  label: string;
  icon: string;
  count: number;
  slots: ActivitySlot[];
}

interface SlotFiltersPanelProps {
  slots: ActivitySlot[];
  templateStartDate: string;
  templateEndDate: string;
  onFilteredSlotsChange: (filteredSlots: ActivitySlot[]) => void;
  children: (props: {
    filteredSlots: ActivitySlot[];
    filtersActive: boolean;
    groupByMode: GroupByMode;
    groups: SlotGroup[];
  }) => React.ReactNode;
}

function formatDateDisplay(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
}

const SlotFiltersPanel: React.FC<SlotFiltersPanelProps> = ({
  slots,
  templateStartDate,
  templateEndDate,
  onFilteredSlotsChange,
  children,
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [groupByMode, setGroupByMode] = useState<GroupByMode>('all');
  const [filters, setFilters] = useState<SlotFilters>({
    startDate: '',
    endDate: '',
    instructorId: '',
    topicId: '',
    roomId: '',
    centreId: '',
    searchText: '',
  });

  // Extract unique values for dropdowns
  const instructors = useMemo(() => {
    const map = new Map<number, string>();
    slots.forEach((s) => {
      if (s.instructorId && s.instructorName) {
        map.set(s.instructorId, s.instructorName);
      }
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [slots]);

  const topics = useMemo(() => {
    const map = new Map<number, string>();
    slots.forEach((s) => {
      if (s.topicMasterId && s.topicName) {
        map.set(s.topicMasterId, s.topicName);
      }
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [slots]);

  const rooms = useMemo(() => {
    const map = new Map<number, string>();
    slots.forEach((s) => {
      if (s.roomId && s.roomName) {
        map.set(s.roomId, s.roomName);
      }
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [slots]);

  const centres = useMemo(() => {
    const map = new Map<number, string>();
    slots.forEach((s) => {
      if (s.centreId && s.centreName) {
        map.set(s.centreId, s.centreName);
      }
    });
    return Array.from(map.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [slots]);

  const filteredSlots = useMemo(() => {
    let result = [...slots];

    if (filters.startDate) {
      result = result.filter((s) => s.date >= filters.startDate);
    }
    if (filters.endDate) {
      result = result.filter((s) => s.date <= filters.endDate);
    }
    if (filters.instructorId) {
      const id = parseInt(filters.instructorId);
      result = result.filter((s) => s.instructorId === id);
    }
    if (filters.topicId) {
      const id = parseInt(filters.topicId);
      result = result.filter((s) => s.topicMasterId === id);
    }
    if (filters.roomId) {
      const id = parseInt(filters.roomId);
      result = result.filter((s) => s.roomId === id);
    }
    if (filters.centreId) {
      const id = parseInt(filters.centreId);
      result = result.filter((s) => s.centreId === id);
    }
    if (filters.searchText) {
      const search = filters.searchText.toLowerCase();
      result = result.filter(
        (s) =>
          s.id.toString().includes(search) ||
          (s.instructorName && s.instructorName.toLowerCase().includes(search)) ||
          (s.topicName && s.topicName.toLowerCase().includes(search)) ||
          (s.roomName && s.roomName.toLowerCase().includes(search)) ||
          (s.centreName && s.centreName.toLowerCase().includes(search)) ||
          (s.agenda && s.agenda.toLowerCase().includes(search))
      );
    }

    return result;
  }, [slots, filters]);

  // Group slots
  const groups = useMemo<SlotGroup[]>(() => {
    const sortByDateTime = (a: ActivitySlot, b: ActivitySlot) =>
      (a.date || '').localeCompare(b.date || '') || (a.startTime || '').localeCompare(b.startTime || '');

    if (groupByMode === 'all') {
      return [{ key: 'all', label: 'All Slots', icon: '📋', count: filteredSlots.length, slots: [...filteredSlots].sort(sortByDateTime) }];
    }

    if (groupByMode === 'by-instructor') {
      const map = new Map<string, ActivitySlot[]>();
      for (const s of filteredSlots) {
        const key = s.instructorName || 'Unassigned';
        const arr = map.get(key) || [];
        arr.push(s);
        map.set(key, arr);
      }
      return Array.from(map.entries())
        .map(([name, slts]) => ({ key: name, label: name, icon: '👨‍🏫', count: slts.length, slots: slts.sort(sortByDateTime) }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    if (groupByMode === 'by-room') {
      const map = new Map<string, ActivitySlot[]>();
      for (const s of filteredSlots) {
        const key = s.roomName || 'No Room';
        const arr = map.get(key) || [];
        arr.push(s);
        map.set(key, arr);
      }
      return Array.from(map.entries())
        .map(([name, slts]) => ({ key: name, label: name, icon: '🏫', count: slts.length, slots: slts.sort(sortByDateTime) }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    if (groupByMode === 'by-centre') {
      const map = new Map<string, ActivitySlot[]>();
      for (const s of filteredSlots) {
        const key = s.centreName || 'No Centre';
        const arr = map.get(key) || [];
        arr.push(s);
        map.set(key, arr);
      }
      return Array.from(map.entries())
        .map(([name, slts]) => ({ key: name, label: name, icon: '📍', count: slts.length, slots: slts.sort(sortByDateTime) }))
        .sort((a, b) => a.label.localeCompare(b.label));
    }

    if (groupByMode === 'by-date') {
      const map = new Map<string, ActivitySlot[]>();
      for (const s of filteredSlots) {
        const key = s.date || 'No Date';
        const arr = map.get(key) || [];
        arr.push(s);
        map.set(key, arr);
      }
      return Array.from(map.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, slts]) => ({
          key: date,
          label: date === 'No Date' ? 'No Date' : formatDateDisplay(date),
          icon: '📅',
          count: slts.length,
          slots: slts.sort((a, b) => (a.startTime || '').localeCompare(b.startTime || '')),
        }));
    }

    return [];
  }, [filteredSlots, groupByMode]);

  // Notify parent when filtered slots change
  React.useEffect(() => {
    onFilteredSlotsChange(filteredSlots);
  }, [filteredSlots]);

  const filtersActive =
    !!filters.startDate ||
    !!filters.endDate ||
    !!filters.instructorId ||
    !!filters.topicId ||
    !!filters.roomId ||
    !!filters.centreId ||
    !!filters.searchText;

  const clearFilters = useCallback(() => {
    setFilters({
      startDate: '',
      endDate: '',
      instructorId: '',
      topicId: '',
      roomId: '',
      centreId: '',
      searchText: '',
    });
  }, []);

  const handleFilterChange = (field: keyof SlotFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const viewModes: { mode: GroupByMode; label: string; icon: string }[] = [
    { mode: 'all', label: 'All Slots', icon: '📋' },
    { mode: 'by-instructor', label: 'By Instructor', icon: '👨‍🏫' },
    { mode: 'by-room', label: 'By Room', icon: '🏫' },
    { mode: 'by-centre', label: 'By Centre', icon: '📍' },
    { mode: 'by-date', label: 'By Date', icon: '📅' },
  ];

  return (
    <div className="slot-filters-wrapper">
      {/* View Mode Selector - Birds View style */}
      <div className="slot-view-selector">
        {viewModes.map((vm) => (
          <button
            key={vm.mode}
            className={`slot-view-btn ${groupByMode === vm.mode ? 'active' : ''}`}
            onClick={() => setGroupByMode(vm.mode)}
          >
            <span className="view-icon">{vm.icon}</span>
            {vm.label}
          </button>
        ))}
      </div>

      {/* Filters Toggle */}
      <div className="slot-filters-toggle">
        <button
          className={`btn-toggle-filters ${showFilters ? 'active' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          {showFilters ? 'Hide Filters' : 'Show Filters'}
          {filtersActive && <span className="filter-badge">{filteredSlots.length}/{slots.length}</span>}
        </button>
      </div>

      {showFilters && (
        <div className="slot-filters-panel">
          <div className="slot-filters-grid">
            <div className="slot-filter-item">
              <label>Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                min={templateStartDate}
                max={templateEndDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="slot-filter-input"
              />
            </div>

            <div className="slot-filter-item">
              <label>End Date</label>
              <input
                type="date"
                value={filters.endDate}
                min={templateStartDate}
                max={templateEndDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="slot-filter-input"
              />
            </div>

            <div className="slot-filter-item">
              <label>Instructor</label>
              <select
                value={filters.instructorId}
                onChange={(e) => handleFilterChange('instructorId', e.target.value)}
                className="slot-filter-select"
              >
                <option value="">All Instructors</option>
                {instructors.map((inst) => (
                  <option key={inst.id} value={inst.id}>
                    {inst.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="slot-filter-item">
              <label>Topic</label>
              <select
                value={filters.topicId}
                onChange={(e) => handleFilterChange('topicId', e.target.value)}
                className="slot-filter-select"
              >
                <option value="">All Topics</option>
                {topics.map((topic) => (
                  <option key={topic.id} value={topic.id}>
                    {topic.name}
                  </option>
                ))}
              </select>
            </div>

            {rooms.length > 0 && (
              <div className="slot-filter-item">
                <label>Room</label>
                <select
                  value={filters.roomId}
                  onChange={(e) => handleFilterChange('roomId', e.target.value)}
                  className="slot-filter-select"
                >
                  <option value="">All Rooms</option>
                  {rooms.map((room) => (
                    <option key={room.id} value={room.id}>
                      {room.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {centres.length > 0 && (
              <div className="slot-filter-item">
                <label>Centre</label>
                <select
                  value={filters.centreId}
                  onChange={(e) => handleFilterChange('centreId', e.target.value)}
                  className="slot-filter-select"
                >
                  <option value="">All Centres</option>
                  {centres.map((centre) => (
                    <option key={centre.id} value={centre.id}>
                      {centre.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="slot-filter-item">
              <label>Search</label>
              <input
                type="text"
                value={filters.searchText}
                onChange={(e) => handleFilterChange('searchText', e.target.value)}
                placeholder="Slot ID, name, room..."
                className="slot-filter-input"
              />
            </div>

            <div className="slot-filter-item slot-filter-actions">
              <button className="btn-clear-filters" onClick={clearFilters}>
                Clear All Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {children({ filteredSlots, filtersActive, groupByMode, groups })}
    </div>
  );
};

export default SlotFiltersPanel;
